import React, { useEffect, useRef, useState } from 'react'
import i18n from 'live/services/i18n'

import GlobalAlert from 'live/components/ui/global-alert'
//import Utils from 'live/lib/utils'
import dateService from 'live/services/date'

import { AggregationState } from '../types'
import useSelection from '../../hooks/useSelection'
import { Action, Actions, Cancel, Column, Columns, Header, Loading, NoAggregations } from '../styles'
import { getStates, start, stop, reprocess, startWithTimespan } from './api'
import Aggregation from './aggregation'
import * as Permission from '../permission'

import { TimespanModal } from './timespan-select-modal'

import { validEventType, isNormalizationEnabledByNormalizer } from 'plugin-liverig/shared/asset/lib/utils'

const columns = [
    { flex: 0, height: '100%', display: 'flex', alignItems: 'center', marginLeft: 16, marginRight: 12 },
    { flex: 1 },
    { flex: 0.5, maxWidth: 400, textAlign: 'center' },
    { flex: 0.3, maxWidth: 300, textAlign: 'center' },
    { flex: 0, margin: '0 16px' }
]

const initialState: { [id: string]: AggregationState } = {}

interface DataAggregationProps {
    selected: {
        asset: {
            id: string
        }
        normalizer: {
            enabled: boolean
        }
    }
    assetType: string
}

const DataAggregation = ({ selected: currentAsset, assetType }: DataAggregationProps) => {
    const { asset, normalizer } = currentAsset
    const normalizerAssetType = assetType === 'wells' ? 'well' : assetType

    const [firstLoad, setFirstLoad] = useState<boolean>(true)
    const [states, setStates] = useState<{ [id: string]: AggregationState }>(initialState)
    const [showReprocessingModal, setShowReprocessingModal] = useState<boolean>(false)
    const [showStartSpanModal, setShowStartSpanModal] = useState<boolean>(false)
    const [showStopAlert, setShowStopAlert] = useState<boolean>(false)
    const [showErrorAlert, setShowErrorAlert] = useState<boolean>(false)
    const [error, setError] = useState<String>("")

    const reprocesSpanRef = useRef<HTMLInputElement>(null)
    const startSpanRef = useRef<HTMLInputElement>(null)

    const [list, toggleItem, toggleAll] = useSelection()

    const normalizerEnabled = isNormalizationEnabledByNormalizer(normalizer)

    useEffect(() => {
        const firstLoadDone = (): void =>
            setFirstLoad((l: boolean) => {
                if (l) {
                    return false
                }

                return l
            })

        async function updateStates(): Promise<void> {
            const { success, states, response } = await getStates(normalizerAssetType, asset.id)
            if (success) {
                setStates(states)
            } else {
                console.error(
                    `Error getting states for asset with ID '${asset.id}' - request status: '${response?.statusText}'`
                )
            }

            firstLoadDone()
        }

        if (normalizer) {
            updateStates()
            const ti = setInterval(() => updateStates(), 3000)

            return (): void => clearInterval(ti)
        } else {
            firstLoadDone()
        }
    }, [asset.id, normalizerEnabled, setStates])

    function setAsLoading(): void {
        setStates((value) => { 
            const newState = {...value}
            Object.keys(value).map((id) => {
                if(list.has(id)) {
                    newState[id] = {...newState[id], state: "LOADING"}
                }
            })
            return newState
        })
    }

    function startAggregation(): void {
        setAsLoading()
        start(normalizerAssetType, asset.id, list, false)
        .then( 
            verifyErrorInJson
        ).catch(processError)
    }

    function startAggregationFromCheckpoint(): void {
        setAsLoading()
        start(normalizerAssetType, asset.id, list, true)
        .then( 
            verifyErrorInJson
        ).catch(processError)
    }

    function startAggregationFromSpan(span: string): void {
        setAsLoading()
        const timezonedSpan = dateService?.prependTimezoneOffset(span)
        startWithTimespan(normalizerAssetType, asset.id, list, timezonedSpan)
        .then( 
            verifyErrorInJson
        ).catch(processError)
    }

    function reprocessAggregation(span: string): void {
        setAsLoading()
        const timezonedSpan = dateService?.prependTimezoneOffset(span)
        reprocess(normalizerAssetType, asset.id, list, timezonedSpan)
        .then( 
            verifyErrorInJson
        ).catch(processError)
    }

    function verifyErrorInJson(result: any) {
        var errors = []
        Object.keys(result).forEach(value => {
            const mess = result[value].message
            if(mess != 'Ok' && !errors.includes(mess)) {
                errors.push(mess)
            }
        })
        if(errors.length > 0) {
            setError(errors.join('; '))
            setShowErrorAlert(true)
        }
    }

    function processError(errorData: any) {
        setError(errorData.message)
        setShowErrorAlert(true)
    }

    function stopAggregation(): void {
        setAsLoading()
        stop(normalizerAssetType, asset.id, list)
        .catch(processError)
        setShowStopAlert(true)
    }

    function submitReprocess(): void {
        reprocessAggregation(reprocesSpanRef?.current?.value || 'none')
        setShowReprocessingModal(false)
    }

    function submitStartAggregation(): void {
        startAggregationFromSpan(startSpanRef?.current?.value || 'none')
        setShowStartSpanModal(false)
    }

    return firstLoad ? (
        <Loading>Loading...</Loading>
    ) : (
        <>
            <GlobalAlert
                message={i18n('Error on request:') + error + '.'}
                className={'alert-sm error'}
                allowClose={true}
                visibleTime={5}
                show={showErrorAlert}
                onClose={() => setShowErrorAlert(false)}
            />
            <GlobalAlert
                message={i18n('Aggregations successfully stopped') + '.'}
                className={'alert-sm info'}
                allowClose={true}
                visibleTime={5}
                show={showStopAlert}
                onClose={() => setShowStopAlert(false)}
            />
            <Actions>
                <Action
                    onClick={(): void => setShowReprocessingModal(true)}
                    className="btn btn-primary"
                    disabled={list.size === 0 || [...list].every(id => states[id]?.state === 'REBUILDING') || !Permission.canManageAggregations()}
                >
                    <i className={'fa fa-database'} />
                    {i18n('Reprocess')} {list.size > 0 && `(${list.size})`}
                </Action>
                <Action
                    onClick={stopAggregation}
                    className="btn"
                    disabled={list.size === 0 || [...list].every(id => states[id]?.state === 'STOPPED') || !Permission.canManageAggregations()}
                >
                    <i className={'fa fa-stop'} /> {i18n('Stop')} {list.size > 0 && `(${list.size})`}
                </Action>
                <Action
                    onClick={startAggregation}
                    className="btn"
                    disabled={list.size === 0 || [...list].every(id => states[id]?.state === 'REALTIME') || !Permission.canManageAggregations() || !normalizerEnabled}
                >
                    <i className={'fa fa-play'} />
                    {i18n('Start (No Reprocess)')} {list.size > 0 && `(${list.size})`}
                </Action>
                <Action
                    onClick={(): void => setShowStartSpanModal(true)}
                    className="btn"
                    disabled={list.size === 0 || [...list].every(id => states[id]?.state === 'REALTIME') || !Permission.canManageAggregations() || !normalizerEnabled}
                >
                    <i className={'fa fa-play'} />
                    {i18n('Start (Select Timespan)')} {list.size > 0 && `(${list.size})`}
                </Action>
                <Action
                    onClick={startAggregationFromCheckpoint}
                    className="btn"
                    disabled={list.size === 0 || [...list].every(id => states[id]?.state === 'REALTIME') || !Permission.canManageAggregations() || !normalizerEnabled}
                >
                    <i className={'fa fa-play'} />
                    {i18n('Start')} {list.size > 0 && `(${list.size})`}
                </Action>
            </Actions>

            <Header>
                <Columns>
                    <Column style={columns[0]}>
                        <input
                            type={'checkbox'}
                            onChange={(): void => toggleAll(Object.keys(states))}
                            checked={list.size > 0 && Object.keys(states).length === list.size}
                        />
                    </Column>
                    <Column style={columns[1]}>{i18n('Name')}</Column>
                    <Column style={columns[2]}>{i18n('Latest event')}</Column>
                    <Column style={columns[3]}>{i18n('Status')}</Column>
                    <Column style={columns[4]} />
                </Columns>
            </Header>

            {Object.keys(states).length ? (
                Object.keys(states)
                    .sort((a,b) => a.localeCompare(b))
                    .map(k => states[k])
                    .map(s => (
                        <Aggregation
                            aggregationState={s}
                            columns={columns}
                            key={s.aggregationDefinition.id}
                            toggleAggregation={toggleItem}
                            selected={list.has(s.aggregationDefinition.id)}
                        />
                    ))
            ) : (
                <NoAggregations>{i18n('No aggregations configured. (If you believe aggregations are configured, please try saving the normalization configurations)')}</NoAggregations>
            )}

            {showReprocessingModal && (
                <TimespanModal
                    modalTitle={i18n('Choose reprocessing span')}
                    spanMessage={i18n('If no span is entered, "none" will be used as default')}
                    showModal={showReprocessingModal}
                    cancelAction={(): void => setShowReprocessingModal(false)}
                    submitAction={(): void => submitReprocess()}
                    refElement={reprocesSpanRef}
                />
            )}

            {showStartSpanModal && (
                 <TimespanModal
                    modalTitle={i18n('Choose processing span')}
                    spanMessage={i18n('If no span is entered, we will assume "None". If a span is entered before the last event it will reprocess from that time.')}
                    showModal={showStartSpanModal}
                    cancelAction={(): void => setShowStartSpanModal(false)}
                    submitAction={(): void => submitStartAggregation()}
                    refElement={startSpanRef}
                />
            )}
        </>
    )
}

export default DataAggregation
